/* eslint-disable i18next/no-literal-string */
import { useEffect, useState } from 'react';

export default function ExpressionLab() {
  const [source, setSource] = useState<File>();
  const [reference, setReference] = useState<File>();
  const [strength, setStrength] = useState(1);
  const [alignment, setAlignment] = useState('legacy');
  const [mouthClose, setMouthClose] = useState(0);
  const [smileReduction, setSmileReduction] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [status, setStatus] = useState('Check worker availability before generating.');
  useEffect(
    () => () => {
      if (result) URL.revokeObjectURL(result);
    },
    [result],
  );

  async function check() {
    try {
      const response = await fetch('/api/experiments/expressions/status');
      if (!response.ok) throw new Error('Access denied or worker endpoint unavailable.');
      const data = (await response.json()) as { available: boolean };
      setStatus(
        data.available
          ? 'Worker configured. Ready to attempt generation.'
          : 'Worker not configured. This prototype needs the separate Python runtime; it is not a browser-only model.',
      );
    } catch (error) {
      setStatus(String(error));
    }
  }
  async function render() {
    if (!source || !reference) return;
    setBusy(true);
    setResult('');
    setStatus('Generating locally on the app server… This may take up to two minutes.');
    const body = new FormData();
    body.set('source', source);
    body.set('reference', reference);
    body.set('strength', String(strength));
    body.set('alignment', alignment);
    body.set('mouthClose', String(mouthClose));
    body.set('smileReduction', String(smileReduction));
    try {
      const response = await fetch('/api/experiments/expressions/render', { method: 'POST', body });
      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error ?? `Request failed (${response.status})`);
      }
      setResult(URL.createObjectURL(await response.blob()));
      setStatus(
        'Generated. Compare expression, identity, mouth detail and blending before accepting.',
      );
    } catch (error) {
      setStatus(String(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-labelledby="expression-lab-title">
      <h2 id="expression-lab-title">Phase 7 — Independent expression lab</h2>
      <p>
        No Snapchat SDK or assets. Experimental LivePortrait expression transfer, not model
        training. Photos are sent to this app’s server, processed temporarily, then deleted. Nothing
        is saved to your profile.
      </p>
      <p>
        Use one front-facing face per photo. Supply an expression you have permission to use. Start
        with the crying reference; other expressions can use the same model.
      </p>
      <p>
        <label>
          Original photo{' '}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={(e) => {
              setSource(e.target.files?.[0]);
              setResult('');
            }}
          />
        </label>
      </p>
      <p>
        <label>
          Expression reference{' '}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={(e) => {
              setReference(e.target.files?.[0]);
              setResult('');
            }}
          />
        </label>
      </p>
      <p>
        <label>
          Face alignment{' '}
          <select value={alignment} disabled={busy} onChange={(e) => setAlignment(e.target.value)}>
            <option value="legacy">Original crop (baseline)</option>
            <option value="landmarks">Landmark alignment (experimental)</option>
          </select>
        </label>
      </p>
      <p>
        <label>
          Expression strength: {strength.toFixed(2)}{' '}
          <input
            type="range"
            min="0"
            max="1.5"
            step="0.05"
            value={strength}
            disabled={busy}
            onChange={(e) => setStrength(Number(e.target.value))}
          />
        </label>
      </p>
      <p>
        <label>
          Reduce remaining smile: {smileReduction.toFixed(2)}{' '}
          <input
            type="range"
            min="0"
            max="0.3"
            step="0.01"
            value={smileReduction}
            disabled={busy}
            onChange={(e) => setSmileReduction(Number(e.target.value))}
          />
        </label>
      </p>
      <p>
        <label>
          Mouth closure: {mouthClose}{' '}
          <input
            type="range"
            min="0"
            max="30"
            step="1"
            value={mouthClose}
            disabled={busy}
            onChange={(e) => setMouthClose(Number(e.target.value))}
          />
        </label>
      </p>
      <p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAlignment('landmarks');
            setStrength(1.3);
            setMouthClose(5);
            setSmileReduction(0.3);
            setResult('');
            setStatus(
              'Crying candidate loaded. Generate to compare; not validated across different people.',
            );
          }}
        >
          Try crying candidate
        </button>{' '}
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAlignment('legacy');
            setStrength(1);
            setMouthClose(0);
            setSmileReduction(0);
            setResult('');
          }}
        >
          Reset controls
        </button>
      </p>
      <button type="button" disabled={busy} onClick={() => void check()}>
        Check worker
      </button>{' '}
      <button type="button" disabled={busy || !source || !reference} onClick={() => void render()}>
        {busy ? 'Generating…' : 'Generate expression'}
      </button>
      <p role="status">{status}</p>
      {result ? (
        <>
          <img
            src={result}
            alt="Experimental expression transfer result"
            style={{ maxWidth: '100%', maxHeight: 650 }}
          />
          <p>
            <a href={result} download="expression.png">
              Download result
            </a>
          </p>
        </>
      ) : null}
    </section>
  );
}
